import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SOURCE_CHAT } from "../../../constants.js";
import {
    setExpiredScheduler,
    setLoadingReply,
} from "../../features/messagesSlice.js";
import useSendData from "../../hooks/useSendData.js";
import useRefreshData from "../../hooks/useRefreshData.js";
import SendMessageButton from "../atoms/buttons/SendMessageButton.jsx";
import TextInput from "../atoms/texts/TextInput.jsx";
import MessageLimit from "../atoms/texts/MessageLimit.jsx";
import { isTokenExpired } from "../../services/tokenService.js";
import VoiceAssistantV2Icon from "../atoms/images/VoiceAssistantV2Icon.jsx";
import { setIsOn } from "../../features/voiceAssistantSlice.js";
import { websocketHandler, startAudio } from "../../services/voiceService.js";

export default function SendMessage() {
    const dispatch = useDispatch();
    const textBoxContainer = useRef(null);
    const expiredAt = useSelector((state) => state.messages.expiredAt);
    const connectedLiveAgent = useSelector(
        (state) => state.liveAgent.connectedLiveAgent
    );
    const retryLiveAgent = useSelector(
        (state) => state.liveAgent.retryLiveAgent
    );
    const liveAgentUnavailable = useSelector(
        (state) => state.liveAgent.liveAgentUnavailable
    );
    const loadingReply = useSelector((state) => state.messages.loadingReply);
    const expiredScheduler = useSelector(
        (state) => state.messages.expiredScheduler
    );
    const isEnd = useSelector((state) => state.messages.isEnd);
    const messages = useSelector((state) => state.messages.messages);
    const messageLimit = useSelector((state) => state.messages.messageLimit);
    const token = useSelector((state) => state.session.token);
    const corrId = useSelector((state) => state.session.corrId);

    const { sendMessage } = useSendData();
    const { handleExpirySchedule, refreshChat } = useRefreshData();

    useEffect(() => {
        if (expiredScheduler) clearInterval(expiredScheduler);
        if (expiredAt && !isEnd) {
            const scheduler = setInterval(() => {
                handleExpirySchedule();
            }, 30000);
            dispatch(setExpiredScheduler(scheduler));
        } else if (isEnd) {
            clearInterval(expiredScheduler);
        }
    }, [expiredAt, isEnd, messages]);

    useEffect(() => {
        if (retryLiveAgent) {
            sendNewMessage(textBoxContainer);
        }
    }, [retryLiveAgent]);

    // Genesys Refresh Chat
    useEffect(() => {
        refreshChat();
    }, [connectedLiveAgent]);

    const sendNewMessage = async (textBoxContainer) => {
        // check if token expired
        const isExpired = isTokenExpired(token);

        if (isExpired) {
            console.log("token expired");
        }

        if (SOURCE_CHAT === "RIB") {
            // Note: Method is declared in parent HTML block
            // eslint-disable-next-line no-undef
            closeTimerDialog();
            // Note: Method is declared in parent HTML block
            // eslint-disable-next-line no-undef
            startSessionTimer();
            // Note: Method is declared in parent HTML block
            // eslint-disable-next-line no-undef
            keepSessionAlive();
        }

        if (!loadingReply || retryLiveAgent) {
            if (!connectedLiveAgent) dispatch(setLoadingReply(true));

            const containerElement = textBoxContainer.current;
            let userInput = containerElement.innerHTML
                .replace(/<div>|<br.*?>/gi, "\n")
                .replace(/<\/div>/g, "")
                .replace(/&nbsp;/g, " ")
                .replace(/&amp;/g, "&")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/&quot;/g, '"')
                .replace(/&apos;/g, "'")
                .replace(/\n/g, "<br>")
                .trim();

            if (retryLiveAgent) userInput = "Retry Live Agent";
            // Mask user input
            userInput = userInput.replace(
                /(\d[\s|\-|\\|/|_|,]?){10,}/g,
                (match) => match.replace(/\d/g, "*")
            );

            containerElement.textContent = "";
            containerElement.focus();

            await sendMessage(userInput);
        }
    };

    const handleVoiceAssistantPermission = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });

            console.log("Microphone enabled:", stream);

            return stream;
        } catch (err) {
            console.error("Error accessing microphone:", err);
        }
    };

    function freshUserId() {
        return Math.floor(10000000 + Math.random() * 90000000).toString();
    }

    const handleVoiceAssistantV3 = async () => {
        const stream = await handleVoiceAssistantPermission();
        startAudio(stream);
        const userId = freshUserId();
        // const tokenTemp =
        //     "eyJ0eXBlIjoiSldUIiwiYWxnIjoiUlMyNTYifQ.eyJzdWIiOiItIiwiY2xpZW50SWQiOiItIiwic2NvcGUiOiJjYXJkLWFwaSIsImdyb3VwcyI6WyJjYXJkLWFwaS11c2VyIl0sInNlc3Npb25JZCI6Ii0iLCJwcmVmZXJyZWRfdXNlcm5hbWUiOiItIiwiY2hhbm5lbENvZGUiOiJzIiwiaXNzIjoiaGxiLWNhcmQtYXBpIiwiYXVkIjoiY2FyZC1hcGktdXNlcnMiLCJpYXQiOjE3NTY2ODg5MzMsImV4cCI6NDkxMDI4ODkzM30.zQPCDyvavsjK7wXYmrNf3o-ZJkSEidEzlJ9DEhKqKVvUZoznCxez7zIH9-yVkgmCo22cD24jSMT0CRs1Y3zx39x5-W8oRIzsW1icmSaPsWDVdAh8zfTL4LcgaH9mLy3WC7EDWg1dcmAM0CeEFO2lcfZLS7OlcnKTsrKl0Pdn525uprTPqx9rZcImiddiO6wSBvOrALj7epee1PzqRGc4jCPZEI0sqGeScvo27spwixy03djILNbFMzRlnPIA3zVXVCXBgTasBcLNN_DTktPlWka_9ZfP7mmf5KepI25A50r1VBdBwKAzpOGUVdmOxjMXp3wzb2nvfTmuBfrb1KDJyw";
        websocketHandler(token, userId);
        dispatch(setIsOn(true));
    };

    return (
        <div className="send-message">
            {!isEnd && !liveAgentUnavailable && (
                <>
                    <div className="text-input">
                        <TextInput
                            sendNewMessage={sendNewMessage}
                            textBoxContainer={textBoxContainer}
                        />
                        <div className="send-button">
                            <SendMessageButton
                                sendNewMessage={() =>
                                    sendNewMessage(textBoxContainer)
                                }
                            />
                        </div>
                        {/* <div
                            className="va-button"
                            onClick={() => handleVoiceAssistantV3()}
                        >
                            <VoiceAssistantV2Icon />
                        </div> */}
                    </div>
                </>
            )}
            {messageLimit && <MessageLimit />}
        </div>
    );
}
